'use client';

import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@eliadmin/core/src/components/ui/tabs';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { BlogCardSkeleton } from './blog-cart-skeleton';
import { BlogCard } from './blog-card';
import { Paragraph } from '../ui/paragraph';
import { Button } from '../ui/button';
import { Heading } from '../ui/heading';

const ALL_CATEGORIES_TAB = 'all';

export function BlogsList({ categories, locale }: { categories: BlogCategory[]; locale: string }) {
  const { t } = useTranslations();

  const [activeTab, setActiveTab] = useState(ALL_CATEGORIES_TAB);
  const activeTabId = activeTab === ALL_CATEGORIES_TAB ? undefined : Number(activeTab);

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, activeTab, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 9,
          order: 'desc',
          orderBy: 'created_at',
          where: activeTabId ? { categories: { equals: activeTabId } } : undefined,
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = blogsQuery?.isLoading;

  return (
    <section
      className={cn(
        'custom-section',
        'mt-8 gap-8 pb-16 sm:mt-10 sm:gap-10 sm:pb-24 lg:mt-12 lg:gap-12 lg:pb-32',
        'pr-0 sm:pr-4',
      )}
    >
      <Heading title={t('text3469')} className={{ container: cn('custom-container') }} />
      <Tabs
        value={activeTab}
        defaultValue={activeTab}
        onValueChange={value => setActiveTab(value)}
        className={cn('custom-container', 'flex flex-col items-start justify-start gap-[50px]')}
      >
        <TabsList
          className={cn(
            'hide-scroll-bar',
            'flex h-fit w-full flex-nowrap items-center justify-start gap-2 overflow-scroll rounded-none bg-transparent py-0 lg:gap-2',
          )}
        >
          <TabsTrigger
            value={ALL_CATEGORIES_TAB}
            className={cn(
              'ea-blogs__tabs-trigger',
              'h-8 whitespace-nowrap rounded-none border px-3 text-sm font-medium uppercase sm:h-9 sm:px-4 lg:h-10 lg:px-[18px]',
            )}
          >
            {t('text1947')}
          </TabsTrigger>
          {categories.map(category => (
            <TabsTrigger
              value={String(category.id)}
              key={category.id}
              className={cn(
                'ea-blogs__tabs-trigger',
                'h-8 whitespace-nowrap rounded-none border px-3 text-sm font-medium uppercase sm:h-9 sm:px-4 lg:h-10 lg:px-[18px]',
              )}
            >
              {category.name}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent
          value={activeTab}
          className="grid w-full grid-cols-1 gap-x-6 gap-y-8 pr-4 sm:grid-cols-2 sm:gap-y-10 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-12"
        >
          {blogsQuery.error && (
            <Paragraph className="col-span-1 pt-8 text-center sm:col-span-2 lg:col-span-3">
              {t('text2364')}
            </Paragraph>
          )}
          {isLoading ? (
            [...Array(9)].map((_, index) => <BlogCardSkeleton key={index} />)
          ) : blogs.length > 0 ? (
            <>
              {blogs.map(blog => (
                <BlogCard key={blog.id} blog={blog} />
              ))}
            </>
          ) : (
            <Paragraph className="col-span-1 text-center sm:col-span-2 sm:text-lg lg:col-span-3">
              {t('text2376')}
            </Paragraph>
          )}
        </TabsContent>
        {blogsQuery.hasNextPage && (
          <div className="flex w-full items-center justify-center pr-4 sm:pr-0">
            <Button
              onClick={() => blogsQuery.fetchNextPage()}
              asChild
              variant={'outline'}
              className="h-[52px] w-full sm:h-14 sm:w-fit lg:h-[60px]"
            >
              {t('text338')}
            </Button>
          </div>
        )}
      </Tabs>
    </section>
  );
}
